import { useGetLibraryConstantsDictionaryData } from '@/providers/ConfigProvider';
import QrScanner from 'qr-scanner';
import React, { useEffect, useRef, useState } from 'react';

interface ScanQRCodeProps {
  onScanned: (value: string) => void;
  onClose: () => void;
  node_locale: string;
}

export const ScanQRCode = ({
  onScanned,
  onClose,
  node_locale,
}: ScanQRCodeProps) => {
  const dictionary = useGetLibraryConstantsDictionaryData();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [scanner, setScanner] = useState<QrScanner | null>(null);

  useEffect(() => {
    if (!videoRef.current) return;

    const qrScanner = new QrScanner(
      videoRef.current,
      (result) => {
        onScanned(result.data);
        qrScanner.stop();
        setScanner(null);
      },
      {
        preferredCamera: 'environment',
        highlightScanRegion: false,
        highlightCodeOutline: false,
      },
    );
    qrScanner.setInversionMode('both');

    qrScanner
      .start()
      .then(() => setScanner(qrScanner))
      .catch((err) => {
        if (err === 'Camera not found.') {
          onScanned('CAMERA_PERMISSION_DENIED');
        } else {
          console.error('QR Scanner error:', err);
        }
      });

    return () => {
      qrScanner.stop();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
      <video
        ref={videoRef}
        className="w-full h-full object-cover bg-transparent"
        playsInline
        muted
      />

      <button
        onClick={() => {
          scanner?.stop();
          onClose();
        }}
        className="absolute right-8 top-8 z-50 rounded-full h-10 w-10 lg:w-12 lg:h-12 lg:right-8 bg-background-primary text-neutral-grey-400"
      >
        ✕
      </button>
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="relative h-[30%] w-[64%] lg:w-[15%] rounded-lg"
          style={{
            width: '200px',
            height: '200px',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)',
          }}
        >
          {/* Top-left corner */}
          <div className="absolute top-0 left-0 h-8 w-8 border-t-[6px] border-l-[6px] border-brand-white rounded-tl-lg" />
          {/* Top-right corner */}
          <div className="absolute top-0 right-0 h-8 w-8 border-t-[6px] border-r-[6px] border-brand-white rounded-tr-lg" />
          {/* Bottom-left corner */}
          <div className="absolute bottom-0 left-0 h-8 w-8 border-b-[6px] border-l-[6px] border-brand-white rounded-bl-lg" />
          {/* Bottom-right corner */}
          <div className="absolute bottom-0 right-0 h-8 w-8 border-b-[6px] border-r-[6px] border-brand-white rounded-br-lg" />
        </div>
      </div>
      <div className="absolute bottom-16 text-center w-[80%] lg:w-[30%] text-text-inverse font-sans">
        <p>
          {dictionary?.qrCodeScanTitleText ??
            'Point your camera at the offer’s QR code.'}
        </p>
        <p>
          {dictionary?.qrCodeScanInfoText ??
            'If you have any queries, get in touch with our staff'}
        </p>
      </div>
    </div>
  );
};
